/** Bake JPEG EXIF rotation into pixels and optionally mirror (selfie preview). */

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to decode photo'));
    img.src = src;
  });
}

/** JPEG EXIF orientation 1–8. 1 = already upright. */
export function jpegExifOrientation(bytes: Uint8Array): number {
  if (bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8) return 1;
  let offset = 2;
  while (offset + 4 < bytes.length) {
    if (bytes[offset] !== 0xff) break;
    const marker = bytes[offset + 1];
    const size = (bytes[offset + 2] << 8) | bytes[offset + 3];
    if (marker === 0xe1 && offset + 4 + size <= bytes.length) {
      const start = offset + 4;
      const isExif =
        bytes[start] === 0x45 &&
        bytes[start + 1] === 0x78 &&
        bytes[start + 2] === 0x69 &&
        bytes[start + 3] === 0x66 &&
        bytes[start + 4] === 0 &&
        bytes[start + 5] === 0;
      if (!isExif) {
        offset += 2 + size;
        continue;
      }
      const tiff = start + 6;
      const little = bytes[tiff] === 0x49 && bytes[tiff + 1] === 0x49;
      const view = (i: number, n: 2 | 4) => {
        let v = 0;
        if (little) {
          for (let b = 0; b < n; b++) v |= bytes[tiff + i + b] << (8 * b);
        } else {
          for (let b = 0; b < n; b++) v = (v << 8) | bytes[tiff + i + b];
        }
        return v;
      };
      const ifd0 = tiff + view(4, 4);
      const entries = view(ifd0 - tiff, 2);
      for (let e = 0; e < entries; e++) {
        const p = ifd0 - tiff + 2 + e * 12;
        const tag = view(p, 2);
        if (tag === 0x0112) return view(p + 8, 2) || 1;
      }
      return 1;
    }
    if (size < 2) break;
    offset += 2 + size;
  }
  return 1;
}

function dataUrlToBytes(dataUrl: string): Uint8Array | null {
  const comma = dataUrl.indexOf(',');
  if (comma < 0) return null;
  try {
    const bin = atob(dataUrl.slice(comma + 1));
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return bytes;
  } catch {
    return null;
  }
}

function canvasToJpeg(canvas: HTMLCanvasElement): string {
  return canvas.toDataURL('image/jpeg', 0.95);
}

/**
 * Draw `src` onto a new canvas, applying EXIF rotation when the bitmap is still
 * landscape-on-file (typical iPhone camera). Then optionally mirror so a selfie
 * matches the in-camera preview ("I see myself").
 */
export async function normalizeShareCameraPhoto(
  dataUrl: string,
  opts: { mirror: boolean },
): Promise<string> {
  const img = await loadImage(dataUrl);
  let srcW = img.naturalWidth || img.width;
  let srcH = img.naturalHeight || img.height;
  if (!(srcW > 0 && srcH > 0)) return dataUrl;

  let bitmap: ImageBitmap | HTMLImageElement = img;
  try {
    const blob = await (await fetch(dataUrl)).blob();
    bitmap = await createImageBitmap(blob, { imageOrientation: 'from-image' } as ImageBitmapOptions);
    srcW = bitmap.width;
    srcH = bitmap.height;
  } catch {
    bitmap = img;
  }

  const bytes = dataUrlToBytes(dataUrl);
  const orientation = bytes ? jpegExifOrientation(bytes) : 1;
  // Only extra-rotate when EXIF says 90° and the decoded bitmap is still landscape
  // (browser did not already apply EXIF).
  const needsQuarterTurn = (orientation === 6 || orientation === 8) && srcW > srcH;
  const rotate90 = needsQuarterTurn ? (orientation === 6 ? 90 : -90) : 0;

  const outW = rotate90 ? srcH : srcW;
  const outH = rotate90 ? srcW : srcH;
  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d');
  if (!ctx) return dataUrl;

  ctx.save();
  if (rotate90 === 90) {
    ctx.translate(outW, 0);
    ctx.rotate(Math.PI / 2);
  } else if (rotate90 === -90) {
    ctx.translate(0, outH);
    ctx.rotate(-Math.PI / 2);
  }
  ctx.drawImage(bitmap, 0, 0, srcW, srcH);
  ctx.restore();
  if ('close' in bitmap && typeof bitmap.close === 'function') bitmap.close();

  if (!opts.mirror) return canvasToJpeg(canvas);

  const mirrored = document.createElement('canvas');
  mirrored.width = canvas.width;
  mirrored.height = canvas.height;
  const mctx = mirrored.getContext('2d');
  if (!mctx) return canvasToJpeg(canvas);
  mctx.translate(mirrored.width, 0);
  mctx.scale(-1, 1);
  mctx.drawImage(canvas, 0, 0);
  return canvasToJpeg(mirrored);
}
